import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useBranch } from "@/context/BranchContext";
import OtpInput from "@/pages/branch/login/components/OtpInput";

type Step = "phone" | "otp" | "done";

const SAUDI_PHONE = /^05\d{8}$/;
const DEMO_OTP = "1234";

export default function ChangePhonePage() {
  const navigate = useNavigate();
  const { employee, updatePhone } = useBranch();

  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");

  if (!employee) {
    return <Navigate to="/" replace />;
  }

  const currentPhone = employee.phone;

  const goBackToAccount = (toast?: string) => {
    navigate("/account", { replace: true, state: toast ? { toast } : null });
  };

  const handlePhone = () => {
    const normalized = phone.replace(/\D/g, "");
    if (!SAUDI_PHONE.test(normalized)) {
      setError("أدخل رقم جوال سعودي صحيح يبدأ بـ 05 من 10 أرقام");
      return;
    }
    if (normalized === currentPhone) {
      setError("هذا هو رقمك الحالي — أدخل رقماً مختلفاً");
      return;
    }
    setError("");
    setStep("otp");
  };

  const handleOtp = (code: string) => {
    if (code.length < 4) return;
    if (code !== DEMO_OTP) {
      setOtpError("رمز التحقق غير صحيح — استخدم 1234 في الوضع التجريبي");
      return;
    }
    setOtpError("");
    updatePhone(phone.replace(/\D/g, ""));
    setStep("done");
  };

  return (
    <div className="max-w-xl mx-auto space-y-4 animate-fade-slide-up">
      {/* شريط الرجوع */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => goBackToAccount()}
          className="inline-flex items-center gap-1.5 h-10 pl-2 pr-3 rounded-btn text-sm font-semibold text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-right-line text-xl"></i>
          الحساب
        </button>
        <h2 className="text-lg font-bold text-foreground-900">تغيير رقم الجوال</h2>
      </div>

      <div className="bg-background-50 rounded-card border border-background-200 p-6 md:p-7">
        {step === "phone" ? (
          <>
            <div className="flex items-start gap-3.5">
              <div className="w-11 h-11 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                <i className="ri-smartphone-line text-xl"></i>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-foreground-900">رقم الجوال الجديد</h3>
                <p className="mt-1.5 text-sm text-foreground-500 leading-relaxed">
                  أدخل رقم الجوال الجديد، ثم سنرسل رمز تحقق تجريبياً للتأكيد.
                </p>
              </div>
            </div>

            <label className="block mt-5">
              <span className="block mb-2 text-sm font-semibold text-foreground-700">رقم الجوال الحالي</span>
              <input
                value={currentPhone}
                readOnly
                dir="ltr"
                className="w-full h-12 rounded-btn border border-background-200 bg-background-100 px-4 text-sm text-foreground-500 outline-none"
              />
            </label>
            <label className="block mt-3">
              <span className="block mb-2 text-sm font-semibold text-foreground-700">رقم الجوال الجديد</span>
              <input
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value.replace(/[^\d]/g, ""));
                  if (error) setError("");
                }}
                inputMode="numeric"
                maxLength={10}
                dir="ltr"
                placeholder="05xxxxxxxx"
                className={`w-full h-12 rounded-btn border px-4 text-sm text-foreground-900 outline-none transition-all ${
                  error
                    ? "border-red-300 focus:ring-2 focus:ring-red-100"
                    : "border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                }`}
              />
            </label>
            {error ? (
              <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
                <i className="ri-error-warning-line text-sm"></i>
                {error}
              </p>
            ) : (
              <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                التحقق من عدم استخدام الرقم في حساب آخر سيتم برمجياً لاحقاً عند ربط النظام.
              </p>
            )}
            <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2.5">
              <button
                type="button"
                onClick={() => goBackToAccount()}
                className="flex-1 h-12 rounded-btn border border-background-300 text-foreground-700 text-sm font-bold hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                إلغاء
              </button>
              <button
                type="button"
                onClick={handlePhone}
                className="flex-1 h-12 rounded-btn bg-primary-500 text-white text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                إرسال رمز التحقق
              </button>
            </div>
          </>
        ) : null}

        {step === "otp" ? (
          <>
            <h3 className="text-base font-bold text-foreground-900">رمز التحقق</h3>
            <p className="mt-1.5 text-sm text-foreground-500 leading-relaxed">
              أرسلنا رمزاً مكوّناً من 4 أرقام إلى{" "}
              <span className="font-semibold text-foreground-700" dir="ltr">
                {phone}
              </span>
            </p>
            <div className="mt-5">
              <OtpInput
                value={otp}
                onChange={(v) => {
                  setOtp(v);
                  if (otpError) setOtpError("");
                }}
                onComplete={handleOtp}
                error={Boolean(otpError)}
              />
            </div>
            {otpError ? (
              <p className="mt-3 text-xs text-red-600 text-center">{otpError}</p>
            ) : (
              <p className="mt-3 text-xs text-foreground-500 text-center">
                الوضع التجريبي — استخدم الرمز <strong dir="ltr">1234</strong>
              </p>
            )}
            <button
              type="button"
              onClick={() => {
                setStep("phone");
                setOtp("");
                setOtpError("");
              }}
              className="mt-4 mx-auto flex items-center gap-1.5 text-sm font-semibold text-foreground-500 hover:text-foreground-700 cursor-pointer"
            >
              <i className="ri-arrow-right-line"></i>
              تعديل رقم الجوال
            </button>
          </>
        ) : null}

        {step === "done" ? (
          <div className="text-center py-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary-500 flex items-center justify-center animate-pop-check">
              <i className="ri-check-line text-3xl text-white"></i>
            </div>
            <h3 className="mt-4 text-base font-bold text-foreground-900">تم تحديث رقم الجوال</h3>
            <p className="mt-1.5 text-sm text-foreground-500">
              رقمك الجديد:{" "}
              <span className="font-semibold text-foreground-700" dir="ltr">
                {phone.replace(/\s/g, "")}
              </span>
            </p>
            <button
              type="button"
              onClick={() => goBackToAccount("تم تحديث رقم الجوال بنجاح")}
              className="mt-6 w-full h-12 rounded-btn bg-primary-500 text-white text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              العودة للحساب
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}